import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  Trash2,
  Pencil,
  Loader2,
  PlusCircle,
  ExternalLink,
  Mic,
  Video,
  Keyboard,
  Sparkles,
  X,
  Save,
  Send,
  Circle,
  Eye,
  MessageCircle,
  Archive,
  ArchiveRestore,
} from 'lucide-react';
import api, { BACKEND_URL, getMyPrayers, updatePrayer, deletePrayer } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import OwnerCommentsDialog from '../components/OwnerCommentsDialog';
import { useConfirmDialog } from '../components/ConfirmDialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';

// Friendly labels for the share option an archived prayer will restore to.
const RESTORE_LABELS = { public: 'Public', circle: 'Circle', unlisted: 'Unlisted' };
const restoreLabel = (p) => RESTORE_LABELS[p.pre_archive_visibility] || 'Unlisted';


const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const ManagePrayers = () => {
  const { user, loading: authLoading, openAuthModal: login } = useAuth();

  const [prayers, setPrayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  // 'active' | 'archived' — which My Prayers tab is showing.
  const [tab, setTab] = useState('active');
  const [selected, setSelected] = useState(() => new Set());
  const [busyIds, setBusyIds] = useState(() => new Set());
  // Shared branded confirmation dialog — replaces `window.confirm`
  // so the browser's "Prevent this page from creating additional
  // dialogs" checkbox can't suppress future deletion prompts.
  const { confirm: confirmDelete, dialog: confirmDialog } = useConfirmDialog();
  const [visibilityBusyIds, setVisibilityBusyIds] = useState(() => new Set());
  const [publishingIds, setPublishingIds] = useState(() => new Set());
  // Undo-delete (client-side): deletes are held for 15 seconds before
  // the actual DELETE call is fired. During that window the row is
  // hidden from the UI and a toast is shown with Undo / Dismiss.
  //   * Undo    → cancel the pending timer, restore the row locally.
  //                No network call is ever made.
  //   * Dismiss → fire the DELETE call immediately.
  //   * Timeout → fire the DELETE call automatically.
  // `undoState` holds either null (no toast) or
  //   { prayers: [...deleted docs], expiresAt: ms, timerId }.
  const [undoState, setUndoState] = useState(null);
  const [undoRemainingMs, setUndoRemainingMs] = useState(0);
  // Ref lets async timer callbacks read the LIVE state without
  // capturing a stale closure over `undoState`.
  const undoStateRef = useRef(null);
  useEffect(() => { undoStateRef.current = undoState; }, [undoState]);
  // Owner-comments dialog: opened per-row from the comment icon. Only one
  // dialog is mounted at a time — the id here identifies the target prayer.
  const [commentsForPrayerId, setCommentsForPrayerId] = useState(null);

  // Edit modal state
  const [editing, setEditing] = useState(null); // prayer object
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getMyPrayers();
      setPrayers(data);
    } catch (e) {
      console.error(e);
      setError('Failed to load your prayers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) refresh();
  }, [user]);

  const activeCount = useMemo(() => prayers.filter(p => p.visibility !== 'archived').length, [prayers]);
  const archivedCount = prayers.length - activeCount;

  const filtered = useMemo(() => {
    const inTab = prayers.filter(p => (p.visibility === 'archived') === (tab === 'archived'));
    const q = query.trim().toLowerCase();
    if (!q) return inTab;
    return inTab.filter(p =>
      (p.title || '').toLowerCase().includes(q) ||
      (p.description || '').toLowerCase().includes(q)
    );
  }, [prayers, query, tab]);

  const allSelected = filtered.length > 0 && filtered.every(p => selected.has(p.id));
  const someSelected = filtered.some(p => selected.has(p.id));

  const toggleOne = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected(prev => {
      if (allSelected) {
        const next = new Set(prev);
        filtered.forEach(p => next.delete(p.id));
        return next;
      }
      const next = new Set(prev);
      filtered.forEach(p => next.add(p.id));
      return next;
    });
  };

  const openEdit = (p) => {
    setEditing(p);
    setEditTitle(p.title || '');
    setEditDescription(p.description || '');
    setEditError(null);
  };
  const closeEdit = () => {
    if (editSaving) return;
    setEditing(null);
  };

  const saveEdit = async () => {
    if (!editing) return;
    const title = editTitle.trim();
    if (!title) { setEditError('Title cannot be empty.'); return; }
    setEditSaving(true);
    setEditError(null);
    try {
      const updated = await updatePrayer(editing.id, {
        title,
        description: editDescription.trim(),
      });
      setPrayers(prev => prev.map(p => (p.id === updated.id ? updated : p)));
      setEditing(null);
    } catch (e) {
      console.error(e);
      setEditError(e.response?.data?.detail || 'Save failed. Please try again.');
    } finally {
      setEditSaving(false);
    }
  };

  const deleteOne = async (id) => {
    // Grab the full doc BEFORE opening the confirm dialog so the
    // preview panel can show the exact prayer title being deleted.
    const target = prayers.find(p => p.id === id);
    const ok = await confirmDelete({
      title: 'Delete this prayer?',
      description: 'This will remove the prayer from Pray Now Together. You have 15 seconds to undo.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      destructive: true,
      preview: target ? { label: "You're deleting", items: [target.title] } : null,
    });
    if (!ok) return;
    if (!target) return;
    // Hide immediately from the list + selection.
    setPrayers(prev => prev.filter(p => p.id !== id));
    setSelected(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    queueUndoableDelete([target]);
  };

  // Archive / Unarchive a single prayer. Archived prayers keep their
  // previous visibility server-side (restored on unarchive) and are
  // hidden everywhere except the Archived tab of this page.
  const toggleVisibility = async (p) => {
    if (visibilityBusyIds.has(p.id)) return;
    const next = p.visibility !== 'archived';
    setVisibilityBusyIds(prev => new Set(prev).add(p.id));
    try {
      const updated = await updatePrayer(p.id, { archived: next });
      setPrayers(prev => prev.map(x => (x.id === updated.id ? updated : x)));
    } catch (e) {
      console.error('archive toggle failed', e);
      alert(e.response?.data?.detail || 'Could not update the archive state. Please try again.');
    } finally {
      setVisibilityBusyIds(prev => {
        const nextSet = new Set(prev);
        nextSet.delete(p.id);
        return nextSet;
      });
    }
  };

  const deleteSelected = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const n = ids.length;
    // Snapshot the docs BEFORE the confirm dialog so we can preview
    // their titles inside the confirmation.
    const idSet = new Set(ids);
    const snapshot = prayers.filter(p => idSet.has(p.id));
    const ok = await confirmDelete({
      title: `Delete ${n} prayer${n === 1 ? '' : 's'}?`,
      description: `${n === 1 ? 'This prayer' : `These ${n} prayers`} will be removed from Pray Now Together. You have 15 seconds to undo.`,
      confirmText: `Delete ${n === 1 ? 'prayer' : 'all'}`,
      cancelText: 'Cancel',
      destructive: true,
      preview: snapshot.length > 0
        ? { label: `You're deleting ${n} prayer${n === 1 ? '' : 's'}`, items: snapshot.map(p => p.title) }
        : null,
    });
    if (!ok) return;
    if (snapshot.length === 0) return;
    setPrayers(prev => prev.filter(p => !idSet.has(p.id)));
    setSelected(new Set());
    queueUndoableDelete(snapshot);
  };

  // Fire the actual DELETE calls for a set of prayers. Called when the
  // Undo window expires OR when the user clicks Dismiss. Failures pop
  // the row back into the list so the user can retry.
  const flushPendingDeletes = async (docs) => {
    if (!docs || docs.length === 0) return;
    const results = await Promise.allSettled(docs.map(p => deletePrayer(p.id)));
    const failed = [];
    results.forEach((r, i) => { if (r.status === 'rejected') failed.push(docs[i]); });
    if (failed.length > 0) {
      console.error('[ManagePrayers] flush delete failed', failed);
      // Restore failed rows to the visible list, sorted newest-first.
      setPrayers(prev => {
        const byId = new Map(prev.map(p => [p.id, p]));
        failed.forEach(p => { if (p && p.id) byId.set(p.id, p); });
        return Array.from(byId.values()).sort((a, b) => {
          const at = a.created_at || ''; const bt = b.created_at || '';
          if (at === bt) return 0;
          return at < bt ? 1 : -1;
        });
      });
      alert(`${docs.length - failed.length} deleted, ${failed.length} could not be removed. Failed ones are back in your list.`);
    }
  };

  // Queue a 15-second undoable delete. If there's already an active
  // undo toast, we merge new docs into it and RESET the timer — this
  // means a rapid burst of deletes stays undoable together, but also
  // holds the earliest ones on-screen longer (which is the desired
  // behaviour for a rapid-fire delete session).
  const queueUndoableDelete = (docs) => {
    if (!docs || docs.length === 0) return;
    const UNDO_WINDOW_MS = 15000;
    setUndoState((prev) => {
      if (prev && prev.timerId != null) clearTimeout(prev.timerId);
      const mergedPrayers = prev ? [...prev.prayers, ...docs] : docs;
      const expiresAt = Date.now() + UNDO_WINDOW_MS;
      // Schedule the auto-flush. When it fires we re-read the LIVE
      // pending list (there may have been more additions since) via
      // the ref, then flush and clear.
      const timerId = setTimeout(() => {
        const live = undoStateRef.current;
        if (!live) return;
        const toFlush = live.prayers;
        setUndoState(null);
        flushPendingDeletes(toFlush);
      }, UNDO_WINDOW_MS);
      return { prayers: mergedPrayers, expiresAt, timerId };
    });
  };

  // Countdown ticker for the visible timer on the toast. Auto-clears
  // once `undoState` goes back to null.
  useEffect(() => {
    if (!undoState) { setUndoRemainingMs(0); return; }
    const tick = () => {
      const rem = undoState.expiresAt - Date.now();
      setUndoRemainingMs(Math.max(0, rem));
    };
    tick();
    const iv = setInterval(tick, 250);
    return () => clearInterval(iv);
  }, [undoState]);

  // Undo: cancel the pending timer, restore all rows locally. No API call.
  const performUndo = () => {
    if (!undoState) return;
    clearTimeout(undoState.timerId);
    const restored = undoState.prayers;
    setUndoState(null);
    setPrayers(prev => {
      const byId = new Map(prev.map(p => [p.id, p]));
      restored.forEach(p => { if (p && p.id) byId.set(p.id, p); });
      return Array.from(byId.values()).sort((a, b) => {
        const at = a.created_at || ''; const bt = b.created_at || '';
        if (at === bt) return 0;
        return at < bt ? 1 : -1;
      });
    });
  };

  // Dismiss: fire the DELETE immediately, close the toast.
  const dismissUndoNow = () => {
    if (!undoState) return;
    clearTimeout(undoState.timerId);
    const toFlush = undoState.prayers;
    setUndoState(null);
    flushPendingDeletes(toFlush);
  };

  // Safety net: if the component unmounts while a delete is pending
  // (user navigates away), flush the pending deletes so the promised
  // action still happens. Uses the ref to avoid a stale closure.
  useEffect(() => {
    return () => {
      const live = undoStateRef.current;
      if (live && live.prayers && live.prayers.length > 0) {
        clearTimeout(live.timerId);
        flushPendingDeletes(live.prayers);
      }
    };
  }, []);

  if (authLoading) {
    return <div className="p-10 text-center text-zinc-500">Loading…</div>;
  }
  if (!user) {
    return (
      <div className="p-10 text-center" data-testid="manage-prayers-signin">
        <p className="text-zinc-600 dark:text-zinc-400 mb-4">Please sign in to manage your prayers.</p>
        <button
          onClick={login}
          data-testid="signin-btn"
          className="h-9 px-4 rounded-full bg-zinc-900 text-white dark:bg-white dark:text-zinc-900 text-sm font-medium"
        >
          Sign in
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6" data-testid="manage-prayers-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-1">
        <h1 className="text-2xl font-bold">Manage prayers</h1>
        <Link
          to="/add-prayer"
          data-testid="new-prayer-btn"
          className="h-9 px-4 rounded-full text-sm font-medium bg-zinc-900 text-white dark:bg-white dark:text-zinc-900 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-colors flex items-center gap-1.5"
        >
          <PlusCircle size={16} /> New prayer
        </Link>
      </div>
      <p className="text-sm text-zinc-500 mb-4">Edit or remove the prayers you&apos;ve shared.</p>

      {/* Active / Archived tabs */}
      <div className="flex items-center gap-1 mb-4 border-b border-zinc-200 dark:border-zinc-800" data-testid="manage-tabs">
        {[
          { key: 'active', label: `Active${activeCount ? ` (${activeCount})` : ''}` },
          { key: 'archived', label: `Archived${archivedCount ? ` (${archivedCount})` : ''}` },
        ].map(t => (
          <button
            key={t.key}
            type="button"
            onClick={() => { setTab(t.key); setSelected(new Set()); }}
            data-testid={`manage-tab-${t.key}`}
            className={`px-4 h-10 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.key
                ? 'border-amber-500 text-zinc-900 dark:text-white'
                : 'border-transparent text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search your prayers"
            data-testid="manage-search-input"
            className="w-full h-10 pl-9 pr-3 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-transparent text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/60"
          />
        </div>
        <div className="text-sm text-zinc-500" data-testid="manage-count">
          {selected.size > 0 ? `${selected.size} selected` : `${filtered.length} prayer${filtered.length === 1 ? '' : 's'}`}
        </div>
        {selected.size > 0 && (
          <button
            onClick={deleteSelected}
            data-testid="bulk-delete-btn"
            className="h-9 px-4 rounded-md text-sm font-medium bg-red-600 text-white hover:bg-red-700 flex items-center gap-1.5"
          >
            <Trash2 size={14} />
            Delete selected
          </button>
        )}
      </div>

      {loading ? (
        <div className="py-16 text-center text-zinc-500 flex items-center justify-center gap-2">
          <Loader2 className="animate-spin" size={16} /> Loading…
        </div>
      ) : error ? (
        <div className="py-10 text-center text-red-600 dark:text-red-400" data-testid="manage-error">
          {error}
          <div className="mt-3">
            <button onClick={refresh} className="text-sm underline">Retry</button>
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center text-zinc-500" data-testid="manage-empty">
          {query ? (
            <>No prayers match &ldquo;{query}&rdquo;.</>
          ) : tab === 'archived' ? (
            <>No archived prayers. Use a prayer&apos;s <span className="font-medium">Archive</span> button to tuck it away here.</>
          ) : (
            <>
              You haven&apos;t shared any prayers yet.{' '}
              <Link to="/add-prayer" className="underline text-zinc-900 dark:text-white font-medium">Share your first prayer</Link>.
            </>
          )}
        </div>
      ) : (
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 overflow-hidden">
          {/* Header row (desktop only) */}
          <div className="hidden md:grid grid-cols-[36px_64px_1fr_120px_140px_140px_88px] items-center gap-3 px-4 py-2 text-xs uppercase tracking-wide text-zinc-500 bg-zinc-50 dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-800">
            <input
              type="checkbox"
              checked={allSelected}
              ref={(el) => { if (el) el.indeterminate = !allSelected && someSelected; }}
              onChange={toggleAll}
              data-testid="toggle-all-checkbox"
              className="rounded"
              aria-label="Select all"
            />
            <span>Cover</span>
            <span>Title</span>
            <span>Type</span>
            <span>Created</span>
            <span>Archive</span>
            <span className="text-right pr-1">Actions</span>
          </div>

          <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
            {filtered.map(p => {
              const hasAudio = Boolean(p.audio_url);
              const hasImage = Boolean(p.image_url);
              const hasVideo = Boolean(p.video_url);
              // Type column now reflects the CREATION METHOD (how the
              // prayer body was produced), not the cover-image status.
              // Prefer the persisted `creation_method` field — falls
              // back to inferring from `video_url` / `audio_url` for
              // legacy prayers written before the field existed. When
              // both are absent we default to "Text" (manual typing).
              const method = (() => {
                if (p.creation_method === 'video' || p.creation_method === 'audio'
                    || p.creation_method === 'assistance' || p.creation_method === 'text') {
                  return p.creation_method;
                }
                if (hasVideo) return 'video';
                if (hasAudio) return 'audio';
                return 'text';
              })();
              const methodMeta = {
                video: { label: 'Video', Icon: Video, tone: 'bg-rose-50 dark:bg-rose-950/40 text-rose-800 dark:text-rose-300' },
                audio: { label: 'Audio', Icon: Mic, tone: 'bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300' },
                assistance: { label: 'Assistance', Icon: Sparkles, tone: 'bg-violet-50 dark:bg-violet-950/40 text-violet-800 dark:text-violet-300' },
                text: { label: 'Text', Icon: Keyboard, tone: 'bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300' },
              }[method];
              const busy = busyIds.has(p.id);
              return (
                <li
                  key={p.id}
                  data-testid={`manage-row-${p.id}`}
                  className="px-3 sm:px-4 py-3 hover:bg-zinc-50 dark:hover:bg-zinc-900/60 transition-colors md:grid md:grid-cols-[36px_64px_1fr_120px_140px_140px_88px] md:items-center md:gap-3"
                >
                  {/* --- MOBILE (< md): stacked card layout --- */}
                  <div className="md:hidden flex gap-3">
                    <input
                      type="checkbox"
                      checked={selected.has(p.id)}
                      onChange={() => toggleOne(p.id)}
                      data-testid={`row-checkbox-mobile-${p.id}`}
                      className="rounded mt-1 flex-shrink-0"
                      aria-label={`Select ${p.title}`}
                    />
                    <Link to={`/prayer/${p.id}`} className="block flex-shrink-0">
                      <div className="w-14 h-14 rounded-md overflow-hidden bg-gradient-to-br from-amber-100 via-rose-100 to-violet-100 dark:from-amber-950/40 dark:via-rose-950/40 dark:to-violet-950/40 flex items-center justify-center">
                        {hasImage ? (
                          <img
                            src={`${BACKEND_URL}${p.image_url}`}
                            alt={p.title}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <span className="text-zinc-500 text-lg">✧</span>
                        )}
                      </div>
                    </Link>
                    <div className="min-w-0 flex-1">
                      <Link to={`/prayer/${p.id}`} className="block text-sm font-medium hover:underline line-clamp-1">
                        {p.title || 'Untitled'}
                      </Link>
                      {p.description && (
                        <div className="text-xs text-zinc-500 line-clamp-1 mt-0.5">{p.description}</div>
                      )}
                      {/* Meta pills row — Type + Share + Date + Views.
                          Flex-wraps naturally on very small screens so
                          nothing gets cut off. */}
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px]">
                        <span
                          data-testid={`row-type-mobile-${p.id}`}
                          className="inline-flex items-center gap-1 text-zinc-600 dark:text-zinc-400"
                        >
                          <methodMeta.Icon size={10} /> {methodMeta.label}
                        </span>
                        {(() => {
                          const isArchived = p.visibility === 'archived';
                          const vBusy = visibilityBusyIds.has(p.id);
                          return (
                            <span className="inline-flex flex-col items-start gap-0.5">
                              <button
                                type="button"
                                onClick={() => toggleVisibility(p)}
                                disabled={vBusy}
                                data-testid={`archive-toggle-mobile-${p.id}`}
                                aria-label={isArchived ? 'Unarchive' : 'Archive'}
                                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border transition-colors disabled:opacity-50 ${
                                  isArchived
                                    ? 'bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800/60'
                                    : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 border-zinc-200 dark:border-zinc-700'
                                }`}
                              >
                                {vBusy ? <Loader2 size={10} className="animate-spin" /> : isArchived ? <ArchiveRestore size={10} /> : <Archive size={10} />}
                                {isArchived ? 'Unarchive' : 'Archive'}
                              </button>
                              {isArchived && (
                                <span className="text-[10px] text-zinc-400" data-testid={`restore-hint-mobile-${p.id}`}>
                                  Restores to {restoreLabel(p)}
                                </span>
                              )}
                            </span>
                          );
                        })()}
                        <span className="text-zinc-500">{fmtDate(p.created_at)}</span>
                        <span
                          className="inline-flex items-center gap-0.5 tabular-nums text-zinc-500"
                          data-testid={`row-views-mobile-${p.id}`}
                          title={`${p.plays_count || 0} views`}
                        >
                          <Eye size={10} /> {p.plays_count || 0}
                        </span>
                      </div>
                      {/* Action buttons — full width, aligned right so
                          they don't crowd the meta row. Larger tap
                          targets (36×36) than the desktop 32px icons. */}
                      <div className="mt-2 flex items-center justify-end gap-0.5">
                        <Link
                          to={`/prayer/${p.id}`}
                          data-testid={`view-btn-mobile-${p.id}`}
                          aria-label="View"
                          className="p-2 rounded-full hover:bg-zinc-200 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-300"
                        >
                          <ExternalLink size={16} />
                        </Link>
                        <button
                          type="button"
                          onClick={() => setCommentsForPrayerId(p.id)}
                          data-testid={`comments-btn-mobile-${p.id}`}
                          aria-label="Manage comments"
                          className="p-2 rounded-full hover:bg-zinc-200 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-300 relative"
                        >
                          <MessageCircle size={16} />
                          {p.owner_comments_count > 0 && (
                            <span className="absolute -top-0.5 -right-0.5 bg-amber-500 text-white text-[9px] font-bold rounded-full min-w-[14px] h-3.5 px-1 flex items-center justify-center">
                              {p.owner_comments_count > 99 ? '99+' : p.owner_comments_count}
                            </span>
                          )}
                        </button>
                        <Link
                          to={`/edit-prayer/${p.id}`}
                          data-testid={`edit-btn-mobile-${p.id}`}
                          aria-label="Edit"
                          className="p-2 rounded-full hover:bg-zinc-200 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-300"
                        >
                          <Pencil size={16} />
                        </Link>
                        <button
                          type="button"
                          onClick={() => deleteOne(p.id)}
                          disabled={busy}
                          data-testid={`delete-btn-mobile-${p.id}`}
                          aria-label="Delete"
                          className="p-2 rounded-full hover:bg-red-50 dark:hover:bg-red-950/40 text-zinc-600 hover:text-red-600 dark:text-zinc-300 disabled:opacity-50"
                        >
                          {busy ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* --- DESKTOP (≥ md): 7-column grid layout --- */}
                  <input
                    type="checkbox"
                    checked={selected.has(p.id)}
                    onChange={() => toggleOne(p.id)}
                    data-testid={`row-checkbox-${p.id}`}
                    className="rounded hidden md:block"
                    aria-label={`Select ${p.title}`}
                  />
                  <Link to={`/prayer/${p.id}`} className="hidden md:block">
                    <div className="w-14 h-14 rounded-md overflow-hidden bg-gradient-to-br from-amber-100 via-rose-100 to-violet-100 dark:from-amber-950/40 dark:via-rose-950/40 dark:to-violet-950/40 flex items-center justify-center">
                      {hasImage ? (
                        <img
                          src={`${BACKEND_URL}${p.image_url}`}
                          alt={p.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          onError={(e) => {
                            const img = e.currentTarget;
                            if (!img.dataset.retried) {
                              img.dataset.retried = '1';
                              setTimeout(() => { img.src = `${BACKEND_URL}${p.image_url}?t=${Date.now()}`; }, 1500);
                            }
                          }}
                        />
                      ) : (
                        <span className="text-zinc-500 text-lg">✧</span>
                      )}
                    </div>
                  </Link>
                  <div className="hidden md:block min-w-0">
                    <Link to={`/prayer/${p.id}`} className="block text-sm font-medium hover:underline line-clamp-1">
                      {p.title || 'Untitled'}
                    </Link>
                    {p.description && (
                      <div className="text-xs text-zinc-500 line-clamp-1 mt-0.5">{p.description}</div>
                    )}
                  </div>
                  <div className="hidden md:block">
                    <span
                      data-testid={`row-type-${p.id}`}
                      data-method={method}
                      className="inline-flex items-center gap-1.5 text-sm text-zinc-700 dark:text-zinc-300"
                    >
                      <methodMeta.Icon size={13} className="text-zinc-500 dark:text-zinc-400" /> {methodMeta.label}
                    </span>
                  </div>
                  <div className="hidden md:flex flex-col text-xs text-zinc-500">
                    <span>{fmtDate(p.created_at)}</span>
                    <span
                      className="inline-flex items-center gap-1 mt-0.5 tabular-nums text-zinc-600 dark:text-zinc-400"
                      data-testid={`row-views-${p.id}`}
                      title={`${p.plays_count || 0} views`}
                    >
                      <Eye size={11} /> {p.plays_count || 0} views
                    </span>
                  </div>
                  <div className="hidden md:block">
                    {(() => {
                      const isArchived = p.visibility === 'archived';
                      const vBusy = visibilityBusyIds.has(p.id);
                      return (
                        <span className="inline-flex flex-col items-start gap-0.5">
                          <button
                            type="button"
                            onClick={() => toggleVisibility(p)}
                            disabled={vBusy}
                            data-testid={`archive-toggle-${p.id}`}
                            aria-label={isArchived ? 'Unarchive' : 'Archive'}
                            title={
                              isArchived
                                ? `Archived — hidden everywhere (feeds, circle, links). Click to unarchive and restore it to ${restoreLabel(p)}.`
                                : 'Click to archive — hides this prayer everywhere. It will only appear in your Archived tab.'
                            }
                            className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors ${
                              isArchived
                                ? 'bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800/60 hover:bg-amber-100 dark:hover:bg-amber-900/60'
                                : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 border-zinc-200 dark:border-zinc-700 hover:bg-zinc-200 dark:hover:bg-zinc-700'
                            } disabled:opacity-50`}
                          >
                            {vBusy ? (
                              <Loader2 size={11} className="animate-spin" />
                            ) : isArchived ? (
                              <ArchiveRestore size={11} />
                            ) : (
                              <Archive size={11} />
                            )}
                            {isArchived ? 'Unarchive' : 'Archive'}
                          </button>
                          {isArchived && (
                            <span className="text-[10px] text-zinc-400" data-testid={`restore-hint-${p.id}`}>
                              Restores to {restoreLabel(p)}
                            </span>
                          )}
                        </span>
                      );
                    })()}
                  </div>
                  <div className="hidden md:flex items-center justify-end gap-1">
                    <Link
                      to={`/prayer/${p.id}`}
                      data-testid={`view-btn-${p.id}`}
                      aria-label="View"
                      className="p-2 rounded-full hover:bg-zinc-200 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-300"
                    >
                      <ExternalLink size={16} />
                    </Link>
                    <button
                      type="button"
                      onClick={() => setCommentsForPrayerId(p.id)}
                      data-testid={`comments-btn-${p.id}`}
                      aria-label="Manage comments"
                      title={`Owner comments (${p.owner_comments_count || 0})`}
                      className="p-2 rounded-full hover:bg-zinc-200 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-300 relative"
                    >
                      <MessageCircle size={16} />
                      {p.owner_comments_count > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 bg-amber-500 text-white text-[9px] font-bold rounded-full min-w-[14px] h-3.5 px-1 flex items-center justify-center">
                          {p.owner_comments_count > 99 ? '99+' : p.owner_comments_count}
                        </span>
                      )}
                    </button>
                    <Link
                      to={`/edit-prayer/${p.id}`}
                      data-testid={`edit-btn-${p.id}`}
                      aria-label="Edit"
                      className="p-2 rounded-full hover:bg-zinc-200 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-300"
                    >
                      <Pencil size={16} />
                    </Link>
                    <button
                      type="button"
                      onClick={() => deleteOne(p.id)}
                      disabled={busy}
                      data-testid={`delete-btn-${p.id}`}
                      aria-label="Delete"
                      className="p-2 rounded-full hover:bg-red-50 dark:hover:bg-red-950/40 text-zinc-600 hover:text-red-600 dark:text-zinc-300 disabled:opacity-50"
                    >
                      {busy ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Edit modal */}
      <Dialog open={Boolean(editing)} onOpenChange={(open) => { if (!open) closeEdit(); }}>
        <DialogContent className="sm:max-w-lg" data-testid="edit-prayer-modal">
          <DialogHeader>
            <DialogTitle>Edit prayer</DialogTitle>
            <DialogDescription>
              Update the title or the text of your prayer. Attached audio and image are not editable here.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-4">
            <div>
              <label className="text-sm font-medium block mb-1">Title <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                maxLength={200}
                data-testid="edit-title-input"
                className="w-full h-10 px-3 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-transparent text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/60"
              />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Prayer text</label>
              <textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                maxLength={2000}
                rows={8}
                data-testid="edit-description-input"
                className="w-full px-3 py-3 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-transparent text-sm resize-none focus:outline-none focus:ring-2 focus:ring-amber-400/60 leading-relaxed"
              />
              <div className="mt-1 text-[11px] text-zinc-400 text-right">{editDescription.length}/2000</div>
            </div>
            {editError && (
              <div className="p-2 rounded-md bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs" data-testid="edit-error">
                {editError}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={closeEdit}
              disabled={editSaving}
              data-testid="cancel-edit-btn"
              className="h-9 px-4 rounded-md text-sm border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-50 flex items-center gap-1"
            >
              <X size={14} /> Cancel
            </button>
            <button
              type="button"
              onClick={saveEdit}
              disabled={editSaving}
              data-testid="save-edit-btn"
              className="h-9 px-4 rounded-md text-sm bg-zinc-900 text-white dark:bg-white dark:text-zinc-900 hover:bg-zinc-800 dark:hover:bg-zinc-200 disabled:opacity-50 flex items-center gap-1"
            >
              {editSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Save
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Owner-comments dialog — one instance, controlled by which prayer id
          is currently selected. Auth-gated: canManage is true because this
          page only lists the current user's own prayers. */}
      <OwnerCommentsDialog
        open={Boolean(commentsForPrayerId)}
        onClose={() => setCommentsForPrayerId(null)}
        prayerId={commentsForPrayerId}
        canManage={true}
        onCountChange={(n) => {
          setPrayers((prev) => prev.map((p) => (p.id === commentsForPrayerId ? { ...p, owner_comments_count: n } : p)));
        }}
      />

      {/* Delete confirmation dialog — the useConfirmDialog hook returns
          a `dialog` node that MUST be embedded in the render tree for
          the AlertDialog to mount. Without this line, calls to
          `confirmDelete()` open an invisible dialog and hang forever. */}
      {confirmDialog}

      {/* Undo-delete toast — 15s window, bottom-center. Renders only
          while `undoState` is populated. The DELETE call is NOT yet
          sent to the server; it fires on timer expiry or when the user
          clicks Dismiss. Undo cancels the pending call entirely. */}
      {undoState && (
        <div
          data-testid="undo-delete-toast"
          className="fixed left-1/2 -translate-x-1/2 bottom-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl border border-zinc-800 bg-zinc-900 text-white shadow-xl max-w-[92vw]"
          role="status"
          aria-live="polite"
        >
          <Trash2 size={16} className="text-red-300 flex-shrink-0" />
          <div className="text-sm min-w-0">
            <div className="font-medium">
              {undoState.prayers.length === 1
                ? '1 prayer will be deleted'
                : `${undoState.prayers.length} prayers will be deleted`}
            </div>
            <div className="text-[11px] text-zinc-400 tabular-nums">
              Undo available for {Math.max(0, Math.ceil(undoRemainingMs / 1000))}s
            </div>
          </div>
          <button
            type="button"
            onClick={performUndo}
            data-testid="undo-delete-btn"
            className="ml-1 h-8 px-3 rounded-full bg-white text-zinc-900 text-xs font-semibold hover:bg-zinc-200 flex items-center gap-1"
          >
            Undo
          </button>
          <button
            type="button"
            onClick={dismissUndoNow}
            data-testid="undo-dismiss-btn"
            aria-label="Delete now"
            title="Delete now"
            className="p-1 rounded-full text-zinc-400 hover:text-white hover:bg-zinc-800"
          >
            <X size={14} />
          </button>
        </div>
      )}
    </div>
  );
};

export default ManagePrayers;
